import {
  useCreateApprovalMatrixMutation,
  useDepartmentOptionsQry,
  useUpdateApprovalMatrixMutation,
  useUserOptionsQry
} from 'mmfintech-backend-api'
import type {
  ApprovalMatrixRequest,
  ApprovalMatrixResponse,
  DepartmentIdRequest,
  DepartmentResponse,
  PortalMerchantUserResponse
} from 'mmfintech-backend-api/types'
import { isValidArray, tr } from 'mmfintech-commons'
import { useEffect, useState } from 'react'
import { BaseApprovalMatrixModal } from '../BaseApprovalMatrixModal'
import { DeleteMatrixModal } from '../DeleteMatrixModal/DeleteMatrixModal'
import { CreateExpensesMatrixTooltip } from './CreateExpensesMatrixTooltip'

import './createEditExpensesMatrixModal.scss'

interface Props {
  department?: DepartmentResponse
  matrix?: ApprovalMatrixResponse
  reload?: () => void
}

export const CreateEditExpensesMatrixModal = ({ department, matrix, reload }: Props) => {
  const { departments, departmentsOptions, departmentsLoading, departmentsError } = useDepartmentOptionsQry()

  const filteredDepartmentsOptions =
    departmentsOptions?.filter((option: { label: string; value: string | number }) =>
      departments.some(
        (dept: DepartmentResponse) => dept.id === Number(option.value) && (!dept.approvalMatrix || matrix)
      )
    ) || []

  const [updateApprovalMatrix, { isLoading: isUpdating, error: updateApprovalMatrixError }] =
    useUpdateApprovalMatrixMutation()
  const [createApprovalMatrix, { isLoading: isCreating, error: createApprovalMatrixError }] =
    useCreateApprovalMatrixMutation()
  const [filteredUsers, setFilteredUsers] = useState<PortalMerchantUserResponse[]>([])

  const { users, usersError, usersFetching } = useUserOptionsQry(1000, ['OWNER', 'ADMINISTRATOR', 'STAFF'])

  const handleCreateMatrix = async (payload: DepartmentIdRequest & { data: ApprovalMatrixRequest }) => {
    return await createApprovalMatrix(payload).unwrap()
  }

  const handleUpdateMatrix = async (payload: DepartmentIdRequest & { data: ApprovalMatrixRequest }) => {
    return await updateApprovalMatrix(payload).unwrap()
  }
  const getSuccessMessages = (): { title: string; subtitle?: string } => {
    let title: string
    let subtitle: string

    if (matrix) {
      title = tr('FRONTEND.EXPENSES_MANAGEMENT.SUCCESS_UPDATE_TITLE', 'Expenses Matrix successfully updated')
      subtitle = tr('FRONTEND.EXPENSES_MANAGEMENT.SUCCESS_UPDATE_SUBTITLE', 'You can now manage it under departments')
    } else {
      title = tr('FRONTEND.EXPENSES_MANAGEMENT.MATRIX_CREATED_TITLE', 'Expenses Matrix successfully created')
      subtitle = tr('FRONTEND.EXPENSES_MANAGEMENT.MATRIX_CREATED_SUBTITLE', 'You can now manage it under departments')
    }

    return { title, subtitle }
  }

  useEffect(() => {
    if (isValidArray(users)) {
      setFilteredUsers(users ? users.filter(user => user.userPermissions.includes('APPROVER')) : [])
    }
  }, [users])

  return (
    <BaseApprovalMatrixModal
      department={department}
      matrix={matrix}
      reload={reload}
      departmentsOptions={filteredDepartmentsOptions}
      departmentsLoading={departmentsLoading}
      users={filteredUsers}
      DeleteModalComponent={DeleteMatrixModal}
      usersFetching={usersFetching}
      createMutation={handleCreateMatrix}
      updateMutation={handleUpdateMatrix}
      isCreating={isCreating}
      isUpdating={isUpdating}
      approversTooltip={<CreateExpensesMatrixTooltip />}
      selectUserDropdownHint={
        <span>
          {tr(
            'FRONTEND.APPROVAL_MATRIX.EXPENSE_DROPDOWN_HINT',
            'Only users with Expense Management approval permission can be added.'
          )}
        </span>
      }
      successMessage={{
        title: getSuccessMessages().title,
        subtitle: getSuccessMessages().subtitle
      }}
      errors={[departmentsError, usersError, createApprovalMatrixError, updateApprovalMatrixError]}
    />
  )
}
